/* eslint-disable max-lines */
import { useContext, useCallback, useMemo } from "react";
import {
    recordNREvent,
    NEWRELIC_TABLES,
    isEmpty,
    AnalyticsContext,
    GroupOrderCartContext,
} from "@swiggy-private/checkout-core";
import { Source, Storage, safeJsonParse } from "@swiggy-private/core-ui";
import {
    impressionAutoApplyCouponPopupEvent,
    impressionOffercashSuccessPopupEvent,
    impressionDelightPopupEvent,
    clickCloseDelightPopupEvent,
    clickTooltipOffercashEvent,
    impressionTippingWidget,
    impressionBillDetailSummary,
    clickTipAmountPredefined,
    clickCustomTipAmount,
    clickOptIn,
    clickInfoIcon,
    impressionSavingsBannerOneLine,
    clickCouponSavingsPopup,
    impressionCutleryNotShown,
    impressionCutleryShown,
    impressionSelfDeliveryInstruction,
    impressionDirectCouponWidgetEvent,
    impressionOfferTooltipText,
    impressionApplyCouponGrouping,
    impressionBillTotalGrouping,
    impressionTippingGrouping,
    impressionDeliveryInstructionGrouping,
    impressionApplyCouponGroupingActual,
    impressionBillTotalGroupingActual,
    impressionTippingGroupingActual,
    impressionDeliveryInstructionGroupingActual,
    impressionDeliveryFeeLineItem,
    impressionDeliveryFeeUnCollapsedLineItem,
    clickBillViewChevron,
    impressionBillViewChevron,
    impressionAddressPSLABanner,
    impressionSavingsStamp,
    impressionCollapsibleBillUpsell,
    impressionEcoSaverToast,
    clickEcoSaverToast,
    impressionEcoSaverLottie,
    clickCouponSavingsPopupWithPOR,
    impressionMoreOffersStrip,
    clickMoreOffersStripEvent,
    clickCouponMaxSavingsPopup,
    clickStealDealAddButtonEvent,
    impressionStealDealWidget,
    impressionOfferCashRewardLineItem,
} from "@cart-client/analytics/events";
import { impressionTimerOnCartEvent } from "@cart-client/analytics/events/TimerOnCart";
import {
    getCartId,
    getIrctcIdentifier,
    getRenderingDetailsItemsPosition,
    getRestaurantId,
    getSavingsBannerData,
    getUserSuperStatus,
    isCartIdSameAsPreviousCartId,
    getBillViewChevronTag,
} from "@cart-client/helpers/Cart";
import { ReviewedCart } from "@cart-client/interfaces/Cart";
import {
    CrossSellTabType,
    IrctcAddressChangeFormKeys,
    SavingsBanner,
    GiftingCartApiResponse,
    CartItemWithUUID,
} from "@swiggy-private/cart-types";
import { OFFER_BUNDLE, PREVIOUS_CART_ID } from "@cart-client/constants/index";
import { RENDERING_DETAILS_KEYS } from "@cart-client/constants/Segmented";
import {
    CarouselItemDetailsParams,
    clickAddItemCarousel,
    ClickAddItemCarouselParams,
    clickChangeTabCarousel,
    clickItemDetailsCarousel,
    ClickItemDetailsCarouselParams,
    impressionAddItemCarousel,
    impressionCarouselItemDetailsPopup,
    WidgetInfo,
} from "@cart-client/analytics/events/CrossSellM2";
import { useCrossSellAnalytics } from "@cart-client/hooks/useCrossSellAnalytics";
import { isOfferBundleTooltip } from "@cart-client/helpers/OfferBundle";
import { clickIrctcEditCta, impressionIrctcUpdateAddress } from "@cart-client/analytics/events/Irctc";
import { clickFreebieClaimButtonEvent, impressionFreebieWidget } from "@cart-client/analytics/events/FreebieWidget";
import { impressionSplashLottie } from "@cart-client/analytics/events/SplashLottie";

// TODO: use context for cart analytics events
export function useCartRendererAnalytics(cartResponse: ReviewedCart | null) {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const savingsBannerData = useMemo(() => getSavingsBannerData(cartResponse), [cartResponse]);
    const userSuperStatus = useMemo(() => getUserSuperStatus(cartResponse) || "", [cartResponse]);
    const { cartId = "", originalCartId = "" } = (cartResponse?.data as GiftingCartApiResponse) || {};
    const { state: groupCartState } = useContext(GroupOrderCartContext);
    const {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        data: { group_cart_id } = {},
    } = groupCartState;

    // analytics handlers
    const impressionAutoApplyCouponPopup = useCallback(() => {
        impressionAutoApplyCouponPopupEvent(impressionEvent);
    }, [impressionEvent]);

    const impressionOffercashSuccessPopup = useCallback(
        (isPHCallout = false) => {
            impressionOffercashSuccessPopupEvent(impressionEvent, isPHCallout);
        },
        [impressionEvent],
    );

    const impressionDelightPopup = useCallback(
        (isPHCallout?: boolean, isDealRush?: boolean, cardType?: string) => {
            impressionDelightPopupEvent(impressionEvent, isPHCallout, isDealRush, cardType);
        },
        [impressionEvent],
    );

    const onClickCloseDelightPopup = useCallback(
        (isDealRush?: boolean, cardType?: string) => {
            clickCloseDelightPopupEvent(clickEvent, isDealRush, cardType);
        },
        [clickEvent],
    );

    const onClickOffercashTooltip = useCallback(
        (couponName: string) => {
            clickTooltipOffercashEvent(couponName, clickEvent);
        },
        [clickEvent],
    );

    const onTimerLoad = useCallback(
        (timerContext?: string) => {
            //TODO: Remove temporary new relic event added for cross-refrencing with analytics events after analysis
            recordNREvent({ eventType: NEWRELIC_TABLES.TIMER_RENDERED, referer: Source.CART });
            impressionTimerOnCartEvent(impressionEvent, timerContext);
        },
        [impressionEvent],
    );

    const onImpressionTippingWidget = useCallback(() => {
        const tipOptions = cartResponse?.data?.tipExperimentData.tipOptions ?? [];

        const mostTippedOption = tipOptions.find((tipOption) => {
            return tipOption.mostTipped;
        });

        mostTippedOption &&
            mostTippedOption.amount &&
            impressionTippingWidget(mostTippedOption?.amount, impressionEvent);
    }, [impressionEvent, cartResponse]);

    const onImpressionBillDetailSummary = useCallback(
        (tag?: string) => {
            impressionBillDetailSummary(impressionEvent, {
                groupCartId: group_cart_id,
                cartId: cartResponse?.data?.cartId,
                addressId: cartResponse?.data?.address_id,
                tag,
            });
        },
        [cartResponse?.data?.address_id, cartResponse?.data?.cartId, group_cart_id, impressionEvent],
    );

    const onClickPredefinedTipAmount = useCallback(
        (optIn: boolean, tipAmount: number) => {
            clickTipAmountPredefined(String(tipAmount), optIn, clickEvent);
        },
        [clickEvent],
    );

    const onClickCustomTipAmount = useCallback(
        (optIn: boolean, tipAmount: number) => {
            clickCustomTipAmount(String(tipAmount), optIn, clickEvent);
        },
        [clickEvent],
    );

    const onClickTipOptIn = useCallback(
        (optIn: boolean, tipAmount: number) => {
            clickOptIn(String(tipAmount), optIn, clickEvent);
        },
        [clickEvent],
    );

    const onClickInfoIcon = useCallback(
        (key: string) => {
            if (isOfferBundleTooltip(key)) {
                return clickInfoIcon("", OFFER_BUNDLE.TOOLTIP_ARD_KEY, clickEvent);
            }
            clickInfoIcon(String(cartResponse?.data?.cartId ?? ""), key, clickEvent);
        },
        [cartResponse, clickEvent],
    );

    const impressionTooltipInfoText = useCallback(
        (key: string) => {
            if (isOfferBundleTooltip(key)) {
                impressionOfferTooltipText(impressionEvent);
            }
        },
        [impressionEvent],
    );

    const onImpressionSavingsOneLine = useCallback(() => {
        if (!isEmpty(savingsBannerData)) {
            impressionSavingsBannerOneLine(savingsBannerData as SavingsBanner, userSuperStatus, impressionEvent, {
                cartId: String(cartId),
                originalCartId: String(originalCartId),
            });
        }
    }, [cartId, impressionEvent, originalCartId, savingsBannerData, userSuperStatus]);

    const onClickCouponSavingsPopup = useCallback(
        (context: string) => {
            clickCouponSavingsPopup(context, clickEvent, groupCartState.data?.group_cart_id);
        },
        [clickEvent, groupCartState.data?.group_cart_id],
    );

    const onClickCouponSavingsPopupWithPOR = useCallback(
        (text: string) => {
            clickCouponSavingsPopupWithPOR(text, clickEvent, groupCartState.data?.group_cart_id);
        },
        [clickEvent, groupCartState.data?.group_cart_id],
    );

    const onClickCouponMaxSavingsPopup = useCallback(
        (text: string) => {
            clickCouponMaxSavingsPopup(text, clickEvent, groupCartState.data?.group_cart_id);
        },
        [clickEvent, groupCartState.data?.group_cart_id],
    );

    const onImpressionCutleryShown = useCallback(() => {
        impressionCutleryShown(impressionEvent);
    }, [impressionEvent]);

    const onImpressionCutleryNotShown = useCallback(() => {
        impressionCutleryNotShown(impressionEvent);
    }, [impressionEvent]);

    const onImpressionAddressPSLABanner = useCallback(
        (delayMessage?: string) => {
            const cartId = cartResponse?.data?.cartId;
            const originalCartId = cartResponse?.data?.originalCartId;
            impressionAddressPSLABanner({ impressionEvent, delayMessage, cartId, originalCartId });
        },
        [cartResponse, impressionEvent],
    );

    const onImpressionSelfDeliveryInstruction = useCallback(() => {
        if (cartResponse) {
            impressionSelfDeliveryInstruction(
                String(cartResponse.data?.cartId || ""),
                getRestaurantId(cartResponse),
                impressionEvent,
            );
        }
    }, [cartResponse, impressionEvent]);

    const onImpressionDirectCouponWidgetEvent = useCallback(
        (couponCode: string, isPaymentCouponWidget?: boolean) => {
            impressionDirectCouponWidgetEvent(impressionEvent, couponCode, group_cart_id, isPaymentCouponWidget);
        },
        [group_cart_id, impressionEvent],
    );

    const onImpressionCouponGrouping = useCallback((): void => {
        if (cartResponse) {
            impressionApplyCouponGrouping(
                impressionEvent,
                getRenderingDetailsItemsPosition(cartResponse, RENDERING_DETAILS_KEYS.APPLY_COUPON_BUTTON),
                getCartId(cartResponse),
            );
        }
    }, [impressionEvent, cartResponse]);

    const onImpressionCouponRemoveGrouping = useCallback((): void => {
        if (cartResponse) {
            impressionApplyCouponGrouping(
                impressionEvent,
                getRenderingDetailsItemsPosition(cartResponse, RENDERING_DETAILS_KEYS.COUPON_REMOVE_BUTTON),
                getCartId(cartResponse),
            );
        }
    }, [impressionEvent, cartResponse]);

    const onImpressionBillTotalGrouping = useCallback((): void => {
        if (cartResponse) {
            impressionBillTotalGrouping(
                impressionEvent,
                getRenderingDetailsItemsPosition(cartResponse, RENDERING_DETAILS_KEYS.BILL_HEADING),
                getCartId(cartResponse),
            );
        }
    }, [impressionEvent, cartResponse]);

    const onImpressionTippingGrouping = useCallback((): void => {
        if (cartResponse) {
            impressionTippingGrouping(
                impressionEvent,
                getRenderingDetailsItemsPosition(cartResponse, RENDERING_DETAILS_KEYS.DE_TIP_CARD_INFORMATION),
                getCartId(cartResponse),
            );
        }
    }, [impressionEvent, cartResponse]);

    const onImpressionDeliveryInstructionGrouping = useCallback((): void => {
        if (cartResponse) {
            impressionDeliveryInstructionGrouping(
                impressionEvent,
                getRenderingDetailsItemsPosition(cartResponse, RENDERING_DETAILS_KEYS.DELIVERY_INSTRUCTIONS),
                getCartId(cartResponse),
            );
        }
    }, [impressionEvent, cartResponse]);
    const onImpressionCouponGroupingActual = useCallback((): void => {
        if (cartResponse) {
            impressionApplyCouponGroupingActual(impressionEvent, getCartId(cartResponse));
        }
    }, [impressionEvent, cartResponse]);

    const onImpressionCouponRemoveGroupingActual = useCallback((): void => {
        if (cartResponse) {
            impressionApplyCouponGroupingActual(impressionEvent, getCartId(cartResponse));
        }
    }, [impressionEvent, cartResponse]);

    const onImpressionBillTotalGroupingActual = useCallback((): void => {
        if (cartResponse) {
            impressionBillTotalGroupingActual(impressionEvent, getCartId(cartResponse));
        }
    }, [impressionEvent, cartResponse]);

    const onImpressionTippingGroupingActual = useCallback((): void => {
        if (cartResponse) {
            impressionTippingGroupingActual(impressionEvent, getCartId(cartResponse));
        }
    }, [impressionEvent, cartResponse]);

    const onImpressionDeliveryInstructionGroupingActual = useCallback((): void => {
        if (cartResponse) {
            impressionDeliveryInstructionGroupingActual(impressionEvent, getCartId(cartResponse));
        }
    }, [impressionEvent, cartResponse]);

    const onClickChangeTabCarousel = useCallback(
        (cartId: string, selectedTab: CrossSellTabType | null) => {
            clickChangeTabCarousel(clickEvent, cartId, selectedTab);
        },
        [clickEvent],
    );

    const onImpressionAddItemCarousel = useCallback(
        (items: string[], cartId: string, selectedTab?: CrossSellTabType | null, widgetInfo?: WidgetInfo): void => {
            impressionAddItemCarousel(impressionEvent, { items, cartId, selectedTab, widgetInfo });
        },
        [impressionEvent],
    );

    const onClickAddItemCarousel = useCallback(
        (params: ClickAddItemCarouselParams): void => {
            clickAddItemCarousel(clickEvent, params);
        },
        [clickEvent],
    );

    const onClickItemDetailsCarousel = useCallback(
        (params: ClickItemDetailsCarouselParams): void => {
            clickItemDetailsCarousel(clickEvent, params);
        },
        [clickEvent],
    );

    const onImpressionCarouselItemDetailsPopup = useCallback(
        (params: CarouselItemDetailsParams): void => {
            impressionCarouselItemDetailsPopup(impressionEvent, params);
        },
        [impressionEvent],
    );

    const {
        onImpressionCartItemDetails,
        onImpressionMagicItemsUnlockPopup,
        onClickDismissMagicItemsUnlockPopup,
        onClickExploreMenuMagicItems,
    } = useCrossSellAnalytics(cartResponse);

    const onImpressionIrctcUpdateAddress = useCallback((): void => {
        const irctcData = getIrctcIdentifier(cartResponse);
        impressionIrctcUpdateAddress(impressionEvent, irctcData);
    }, [cartResponse, impressionEvent]);

    const onClickIrctcEditCta = useCallback(
        (ctaType: IrctcAddressChangeFormKeys): void => {
            const irctcData = getIrctcIdentifier(cartResponse);
            clickIrctcEditCta(clickEvent, ctaType, irctcData);
        },
        [cartResponse, clickEvent],
    );

    const onImpressionDeliveryFeeLineItem = useCallback(
        (label: string): void => {
            try {
                const cartId = cartResponse?.data?.cartId;
                const originalCartId = cartResponse?.data?.originalCartId;
                const newLabel = { ...(safeJsonParse(label) as object), cartId, originalCartId };

                if (!isCartIdSameAsPreviousCartId(String(cartId))) {
                    impressionDeliveryFeeLineItem(impressionEvent, JSON.stringify(newLabel));
                }
                Storage.setItem(PREVIOUS_CART_ID, String(cartId));
            } catch {
                void 0;
            }
        },
        [impressionEvent, cartResponse?.data?.cartId, cartResponse?.data?.originalCartId],
    );

    const onImpressionOfferCashRewardLineItem = useCallback(
        (impressionData: string): void => {
            impressionOfferCashRewardLineItem(impressionEvent, impressionData, cartResponse?.data?.cartId);
        },
        [impressionEvent, cartResponse?.data?.cartId],
    );

    const onImpressionDeliveryFeeUnCollapsedLineItem = useCallback(
        (label: string): void => {
            try {
                const cartId = cartResponse?.data?.cartId;
                const originalCartId = cartResponse?.data?.originalCartId;
                const newLabel = { details: safeJsonParse(label), cartId, originalCartId };

                impressionDeliveryFeeUnCollapsedLineItem(impressionEvent, JSON.stringify(newLabel));
            } catch {
                void 0;
            }
        },
        [impressionEvent, cartResponse?.data?.cartId, cartResponse?.data?.originalCartId],
    );

    const onImpressionBillViewChevron = useCallback(
        (context: Record<string, unknown>) => {
            if (!isEmpty(cartResponse) && cartResponse) {
                const tag = getBillViewChevronTag(cartResponse);
                const contextWithTag = tag ? { ...context, tag } : context;
                impressionBillViewChevron(impressionEvent, getCartId(cartResponse), contextWithTag);
            }
        },
        [cartResponse, impressionEvent],
    );

    const onImpressionCollapsibleBillUpsell = useCallback(
        (upsellMessage: string) => {
            if (!isEmpty(cartResponse) && cartResponse) {
                impressionCollapsibleBillUpsell(impressionEvent, getCartId(cartResponse), upsellMessage);
            }
        },
        [cartResponse, impressionEvent],
    );

    const onClickBillViewChevron = useCallback(
        (context: Record<string, unknown>) => {
            if (!isEmpty(cartResponse) && cartResponse) {
                clickBillViewChevron(clickEvent, getCartId(cartResponse), context);
            }
        },
        [cartResponse, clickEvent],
    );

    const onImpressionSavingsStamp = useCallback(
        (context: Record<string, unknown>) => {
            if (!isEmpty(cartResponse) && cartResponse) {
                impressionSavingsStamp(
                    impressionEvent,
                    getCartId(cartResponse),
                    getRestaurantId(cartResponse),
                    context,
                );
            }
        },
        [cartResponse, impressionEvent],
    );

    const onImpressionEcoSaverToast = useCallback(
        (cartId?: number, message?: string, addressId?: string) => {
            impressionEcoSaverToast(impressionEvent, cartId, message, addressId);
        },
        [impressionEvent],
    );

    const onImpressionEcoSaverLottie = useCallback(
        (cartId?: number, addressId?: string, lottieId?: string) => {
            impressionEcoSaverLottie(impressionEvent, cartId, addressId, lottieId);
        },
        [impressionEvent],
    );

    const onClickEcoSaverToast = useCallback(
        (cartId?: number, message?: string, addressId?: string) => {
            clickEcoSaverToast(clickEvent, cartId, message, addressId);
        },
        [clickEvent],
    );

    const onImpressionMoreOffersStrip = useCallback(
        (data: Record<string, unknown>) => {
            impressionMoreOffersStrip(impressionEvent, data);
        },
        [impressionEvent],
    );

    const onClickMoreOffersStrip = useCallback(() => {
        clickMoreOffersStripEvent(clickEvent);
    }, [clickEvent]);

    const onImpressionFreebieWidget = useCallback(
        (params: {
            cartId: string;
            originalCartId: string;
            itemId: string;
            freebieUnlocked: string;
            itemType: string;
        }) => {
            impressionFreebieWidget(impressionEvent, params);
        },
        [impressionEvent],
    );

    const onClickFreebieClaimButton = useCallback(
        (itemId: string, itemType: string) => {
            clickFreebieClaimButtonEvent(clickEvent, itemId, itemType);
        },
        [clickEvent],
    );

    const onImpressionStealDealWidget = useCallback(
        (cartId: string, originalCartId: string, items: CartItemWithUUID[], stealDealUnlocked: boolean) => {
            impressionStealDealWidget(impressionEvent, { cartId, originalCartId, items, stealDealUnlocked });
        },
        [impressionEvent],
    );
    const onClickStealDealAddButton = useCallback(
        (cartId: string, originalCartId: string, item: CartItemWithUUID) => {
            clickStealDealAddButtonEvent(clickEvent, { cartId, originalCartId, item });
        },
        [clickEvent],
    );

    const onImpressionSplashLottie = useCallback(
        (tag: string) => {
            impressionSplashLottie(
                impressionEvent,
                JSON.stringify({
                    cartId,
                    tag,
                }),
            );
        },
        [cartId, impressionEvent],
    );

    return {
        impressionAutoApplyCouponPopup,
        impressionOffercashSuccessPopup,
        impressionDelightPopup,
        onClickCloseDelightPopup,
        onClickOffercashTooltip,
        onTimerLoad,
        onImpressionTippingWidget,
        onImpressionBillDetailSummary,
        onClickPredefinedTipAmount,
        onClickCustomTipAmount,
        onClickTipOptIn,
        onClickInfoIcon,
        onImpressionSavingsOneLine,
        onClickCouponSavingsPopup,
        onClickCouponSavingsPopupWithPOR,
        onImpressionCutleryShown,
        onImpressionCutleryNotShown,
        onImpressionSelfDeliveryInstruction,
        onImpressionDirectCouponWidgetEvent,
        impressionTooltipInfoText,
        onImpressionCouponGrouping,
        onImpressionBillTotalGrouping,
        onImpressionTippingGrouping,
        onImpressionDeliveryInstructionGrouping,
        onImpressionCouponRemoveGrouping,
        onImpressionCouponGroupingActual,
        onImpressionBillTotalGroupingActual,
        onImpressionTippingGroupingActual,
        onImpressionDeliveryInstructionGroupingActual,
        onImpressionCouponRemoveGroupingActual,
        onImpressionAddItemCarousel,
        onClickAddItemCarousel,
        onClickItemDetailsCarousel,
        onImpressionCarouselItemDetailsPopup,
        onImpressionCartItemDetails,
        onImpressionMagicItemsUnlockPopup,
        onClickDismissMagicItemsUnlockPopup,
        onClickExploreMenuMagicItems,
        onImpressionIrctcUpdateAddress,
        onClickIrctcEditCta,
        onImpressionDeliveryFeeLineItem,
        onImpressionDeliveryFeeUnCollapsedLineItem,
        onImpressionBillViewChevron,
        onClickBillViewChevron,
        onImpressionAddressPSLABanner,
        onImpressionSavingsStamp,
        onImpressionCollapsibleBillUpsell,
        onClickChangeTabCarousel,
        onImpressionEcoSaverToast,
        onImpressionEcoSaverLottie,
        onClickEcoSaverToast,
        onImpressionMoreOffersStrip,
        onClickMoreOffersStrip,
        onClickCouponMaxSavingsPopup,
        onImpressionFreebieWidget,
        onClickFreebieClaimButton,
        onImpressionStealDealWidget,
        onClickStealDealAddButton,
        onImpressionOfferCashRewardLineItem,
        onImpressionSplashLottie,
    };
}
